"use client"

import { useEffect, useId, useState, type ReactNode } from "react"
import type { LucideIcon } from "lucide-react"

import { Label } from "@/common/components/ui/label"
import { Slider } from "@/common/components/ui/slider"
import { Switch } from "@/common/components/ui/switch"
import { cn } from "@/common/lib/utils"
import { REASON_VAR } from "../charts/palette"

/**
 * The control vocabulary for the parameter page.
 *
 * Kept to four shapes on purpose. Forty knobs rendered forty different ways is
 * a wall of noise; forty knobs rendered four ways is a page someone can scan.
 * The layout follows the existing lever panel — label left, live value right,
 * control beneath — so the two surfaces read as one product.
 */

export function Cluster({
    id,
    sectionRef,
    icon: Icon,
    title,
    hint,
    inactive,
    inactiveNote,
    writtenBy,
    changed,
    children,
}: {
    id?: string
    sectionRef?: (node: HTMLElement | null) => void
    icon: LucideIcon
    title: string
    hint?: string
    /** Dimmed and non-interactive when another setting makes it meaningless. */
    inactive?: boolean
    inactiveNote?: string
    /** Framework that wrote settings in here — the link between the two tabs. */
    writtenBy?: string
    /** How many settings differ from standard. */
    changed?: number
    children: ReactNode
}) {
    // `writtenBy` persists for as long as the framework is selected; the cue
    // should not. Armed by adjusting state during render when the writer
    // changes — the pattern `modules/analytics/components/animated-number.tsx`
    // uses — rather than from an effect body, which would cascade a second
    // render on every mount. The timer only ever turns it back off.
    const [flash, setFlash] = useState(false)
    const [seenWriter, setSeenWriter] = useState(writtenBy)
    if (writtenBy !== seenWriter) {
        setSeenWriter(writtenBy)
        setFlash(Boolean(writtenBy))
    }
    useEffect(() => {
        if (!flash) return
        const timer = window.setTimeout(() => setFlash(false), 1800)
        return () => window.clearTimeout(timer)
    }, [flash])

    return (
        <section
            id={id}
            ref={sectionRef}
            data-section={id}
            className={cn(
                "flex flex-col scroll-mt-24 lp-card rounded-xl border bg-card transition-opacity",
                inactive && "opacity-55",
                // One-shot attention cue when a framework writes here. It used
                // to be keyed off `writtenBy` alone, which is derived from
                // persistent selection state — so the card pulsed for as long as
                // the framework stayed selected, i.e. forever. `flash` is a
                // timer, so the cue now actually ends. Under reduced motion the
                // animation is dropped but the chip below stays: a cue that
                // renders as nothing at all is a missing feature, not an
                // accessible fallback.
                flash && "motion-safe:animate-pulse-subtle",
            )}
        >
            <header className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
                <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                <h3 className="text-sm font-medium">{title}</h3>
                {/* Both, not either. The count used to be suppressed whenever a
                    framework had written here, so once a preset touched a
                    cluster you could no longer see how many settings differed —
                    including after editing it by hand. The rail badge kept
                    showing it, so the two disagreed. */}
                <span className="ml-auto flex shrink-0 items-center gap-1.5">
                    {writtenBy && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            {writtenBy}
                        </span>
                    )}
                    {changed !== undefined && changed > 0 && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {changed}
                        </span>
                    )}
                </span>
            </header>
            {/* Actually inert, not just faded. `opacity-55` alone left every
                control in a "switched off" cluster clickable, so a slider could
                be moved in a section the engine ignores. */}
            <div
                className={cn("space-y-4 p-4", inactive && "pointer-events-none")}
                aria-disabled={inactive || undefined}
            >
                {hint && <p className="-mt-1 text-xs text-muted-foreground">{hint}</p>}
                {inactive && inactiveNote && (
                    <p className="rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground">
                        {inactiveNote}
                    </p>
                )}
                {children}
            </div>
        </section>
    )
}

export function SliderRow({
    label,
    hint,
    value,
    min,
    max,
    step = 1,
    unit,
    format,
    onChange,
}: {
    label: string
    hint?: string
    value: number
    min: number
    max: number
    step?: number
    unit?: string
    format?: (value: number) => string
    onChange: (value: number) => void
}) {
    // Radix renders the thumb, not the root, as the focusable slider, so a
    // `<Label htmlFor>` cannot reach it. `aria-label` is what actually names it;
    // without one all ~25 sliders announced as an unnamed "slider".
    const id = useId()
    return (
        <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3">
                <Label id={id} className="text-xs font-normal">
                    {label}
                </Label>
                <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {format ? format(value) : value}
                    {unit ? ` ${unit}` : ""}
                </span>
            </div>
            <Slider
                value={[value]}
                min={min}
                max={max}
                step={step}
                aria-labelledby={id}
                onValueChange={([next]) => onChange(next)}
            />
            {hint && <p className="text-xs leading-snug text-muted-foreground">{hint}</p>}
        </div>
    )
}

export function SwitchRow({
    label,
    hint,
    checked,
    onChange,
}: {
    label: string
    hint?: string
    checked: boolean
    onChange: (checked: boolean) => void
}) {
    return (
        <div className="space-y-1">
            <div className="flex items-start justify-between gap-3">
                <Label className="text-xs font-normal leading-snug">{label}</Label>
                <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
            </div>
            {hint && <p className="pr-12 text-xs leading-snug text-muted-foreground">{hint}</p>}
        </div>
    )
}

export function ChoiceRow<T extends string>({
    label,
    hint,
    value,
    options,
    onChange,
}: {
    label: string
    hint?: string
    value: T
    options: Array<{ value: T; label: string }>
    onChange: (value: T) => void
}) {
    // A tray of plain buttons announced as buttons with no grouping and no
    // selected state — the choice was carried by background colour alone.
    const id = useId()
    return (
        <div className="space-y-1.5">
            <Label id={id} className="text-xs font-normal">
                {label}
            </Label>
            <div
                role="radiogroup"
                aria-labelledby={id}
                className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1"
            >
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={option.value === value}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            "flex-1 whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors",
                            option.value === value
                                ? "bg-background font-medium shadow-sm"
                                : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            {hint && <p className="text-xs leading-snug text-muted-foreground">{hint}</p>}
        </div>
    )
}

/**
 * A 100 %-stacked bar of weights that stays readable at any distribution.
 *
 * Weights are absolute in the engine, but what matters when tuning them is the
 * RATIO — "regulatory duties count four times what a career wish counts" is the
 * decision being made, and two sliders in isolation do not show it. So the bar
 * renders normalised shares while the sliders below edit the underlying values.
 *
 * A segment below ~7 % gets no inline label rather than a clipped one; the
 * legend underneath carries every value regardless, so nothing depends on the
 * bar being wide enough.
 */
export function WeightBar({
    entries,
}: {
    entries: Array<{ id: string; label: string; value: number; colourVar?: string }>
}) {
    const total = entries.reduce((sum, entry) => sum + entry.value, 0)
    const fallback = ["var(--lp-pflicht)", "var(--lp-voraussetzung)", "var(--lp-luecke)", "var(--lp-ziel)"]

    return (
        <div className="space-y-2">
            <div className="flex h-7 w-full overflow-hidden rounded-md" role="presentation">
                {total === 0 ? (
                    <div className="flex-1 bg-muted" />
                ) : (
                    entries.map((entry, index) => {
                        const share = (entry.value / total) * 100
                        if (share <= 0) return null
                        return (
                            <div
                                key={entry.id}
                                className="flex items-center justify-center overflow-hidden text-xs font-medium text-white"
                                style={{
                                    width: `${share}%`,
                                    background: entry.colourVar ?? fallback[index % fallback.length],
                                }}
                                title={`${entry.label}: ${Math.round(share)} %`}
                            >
                                {share >= 7 ? `${Math.round(share)} %` : ""}
                            </div>
                        )
                    })
                )}
            </div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {entries.map((entry, index) => (
                    <li key={entry.id} className="flex items-center gap-1.5 text-xs">
                        <span
                            aria-hidden
                            className="size-2 shrink-0 rounded-sm"
                            style={{ background: entry.colourVar ?? fallback[index % fallback.length] }}
                        />
                        <span className="truncate text-muted-foreground">{entry.label}</span>
                        <span className="ml-auto shrink-0 font-medium tabular-nums">{entry.value}</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const REASON_COLOURS = REASON_VAR
